import type { QueryClient } from '@tanstack/react-query';

import { collectNodesInCache } from 'api/decomposition-tree/utils/treeCacheLookups';

import type { RichTreeItem } from '../../types';
import { getDataNodeIdFromTreeId, getParentTreeId } from '../../utils/treeId';
import { findPathToNodeByDataNodeId } from './treeItem';

function normalizeParentId(parentId: string | null | undefined): string | null {
  const trimmed = parentId?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function parentDataNodeIdFromTreePath(path: string[]): string | null {
  if (path.length <= 1) return null;
  return getDataNodeIdFromTreeId(path[path.length - 2]);
}

/** Parent dataNodeId captured when the move dialog opens (`null` for a root node). */
export function getInitialParentDataNodeId(moveNodeId: string): string | null {
  const parentTreeId = getParentTreeId(moveNodeId);
  return parentTreeId ? getDataNodeIdFromTreeId(parentTreeId) : null;
}

/**
 * Returns true when the node no longer sits under its original parent — e.g.
 * another user moved it while the move dialog was open.
 */
export function isMoveNodeStale(
  queryClient: QueryClient,
  projectId: string,
  templateNodeId: string,
  proposalId: string | undefined,
  nodeDataNodeId: string,
  initialParentDataNodeId: string | null,
  liveTreeItems: RichTreeItem[]
): boolean {
  const initialParent = normalizeParentId(initialParentDataNodeId);
  const cacheCopies = collectNodesInCache(queryClient, { projectId, templateNodeId, proposalId }, nodeDataNodeId);

  // 1. Any cached copy under a different parent means it was re-parented — even
  //    if the (lagging) live tree still shows it under the original parent.
  if (cacheCopies.some((node) => normalizeParentId(node.parentId) !== initialParent)) return true;

  // 2. Otherwise trust the live tree when the node is visible in it.
  const livePath = findPathToNodeByDataNodeId(liveTreeItems, nodeDataNodeId);
  if (livePath) {
    return normalizeParentId(parentDataNodeIdFromTreePath(livePath)) !== initialParent;
  }

  // 3. Not in the live tree. Every remaining cache copy matches the original
  //    parent (step 1 returned otherwise), so it's stale only if it has also
  //    vanished from the cache entirely — i.e. moved/deleted elsewhere.
  return cacheCopies.length === 0;
}
